// src/services/googleTranslate.js
// @ts-nocheck

const GT_SRC =
  "https://translate.google.com/translate_a/element.js?cb=__googleTranslateElementInit";

export class GoogleTranslateService {
  constructor({
    pageLanguage = "fr",
    includedLanguages = ["fr", "en", "ar"],
    containerId = "google_translate_element",
    storageKey = "app_lang",
    debounceMs = 400,
    loadTimeoutMs = 12000,
  } = {}) {
    this.pageLanguage = pageLanguage;
    this.includedLanguages = includedLanguages;
    this.containerId = containerId;
    this.storageKey = storageKey;
    this.debounceMs = debounceMs;
    this.loadTimeoutMs = loadTimeoutMs;

    this._loaded = false;
    this._loadingPromise = null;
    this._inited = false;

    this._observer = null;
    this._timer = null;
    this._ignore = false;

    this._current = this._getSaved() || this.pageLanguage;

    this._installHardHideCss();
    this._installGlobalErrorLogger();
  }

  /* -------------------- persistence -------------------- */
  _getSaved() {
    try {
      return localStorage.getItem(this.storageKey);
    } catch {
      return null;
    }
  }
  _save(lang) {
    try {
      localStorage.setItem(this.storageKey, lang);
    } catch {
      // ignore
    }
  }

  /* -------------------- UI Google: hard hidden -------------------- */
  _installHardHideCss() {
    if (document.getElementById("__gt_hard_hide_css")) return;

    const style = document.createElement("style");
    style.id = "__gt_hard_hide_css";
    style.textContent = `
      /* Hide ALL Google Translate UI elements */
      .goog-te-banner-frame.skiptranslate,
      iframe.goog-te-banner-frame,
      #goog-gt-tt,
      .goog-te-balloon-frame,
      .goog-te-gadget,
      .goog-te-gadget-icon,
      .goog-te-menu-frame,
      .goog-logo-link,
      .goog-te-gadget span,
      .goog-te-gadget-simple,
      .goog-text-highlight {
        display: none !important;
        visibility: hidden !important;
        height: 0 !important;
        width: 0 !important;
        border: 0 !important;
      }

      /* Google sometimes pushes body down */
      body { top: 0px !important; position: static !important; }

      /* Keep app visible if GT wraps nodes */
      .skiptranslate { display: initial !important; }
    `;
    document.head.appendChild(style);
  }

  _cleanupGoogleUiNodes() {
    // Remove tooltip if created
    const tt = document.getElementById("goog-gt-tt");
    if (tt) tt.remove();

    // Hide banner frame if inserted
    document.querySelectorAll("iframe.goog-te-banner-frame").forEach((el) => {
      el.style.display = "none";
      el.style.visibility = "hidden";
      el.style.height = "0";
      el.style.width = "0";
    });

    // Reset body offset (Google sets inline sometimes)
    try {
      document.body.style.top = "0px";
    } catch {
      // ignore
    }
  }

  _installGlobalErrorLogger() {
    if (window.__gt_error_logger_installed) return;
    window.__gt_error_logger_installed = true;

    window.addEventListener("error", (e) => {
      const file = e?.filename || "";
      if (
        file.includes("translate") ||
        (e?.message || "").toLowerCase().includes("script error")
      ) {
        console.warn("Google Translate error (likely blocked by AdBlock/CSP).", {
          message: e?.message,
          filename: e?.filename,
          lineno: e?.lineno,
          colno: e?.colno,
        });
      }
    });

    window.addEventListener("unhandledrejection", (e) => {
      console.warn("Unhandled promise rejection:", e?.reason);
    });
  }

  /* -------------------- container -------------------- */
  _ensureContainer() {
    if (document.getElementById(this.containerId)) return;
    const el = document.createElement("div");
    el.id = this.containerId;
    el.style.display = "none";
    document.body.appendChild(el);
  }

  /* -------------------- load (safe) -------------------- */
  async load() {
    if (this._loaded) return true;
    if (this._loadingPromise) return this._loadingPromise;

    this._loadingPromise = new Promise((resolve) => {
      this._ensureContainer();
      this._cleanupGoogleUiNodes();

      const finish = (ok, reason) => {
        this._loaded = !!ok;
        if (!ok) {
          console.warn("GT load failed:", reason || "unknown (blocked/adblock/csp)");
        }
        resolve(!!ok);
      };

      // If already available
      if (window.google?.translate?.TranslateElement) {
        try {
          this._initOnce();
          finish(true);
        } catch (e) {
          finish(false, e);
        }
        return;
      }

      // Global callback used by Google
      window.__googleTranslateElementInit = () => {
        try {
          this._initOnce();
          finish(true);
        } catch (e) {
          finish(false, e);
        }
      };

      let script = document.querySelector(`script[src="${GT_SRC}"]`);
      if (!script) {
        script = document.createElement("script");
        script.src = GT_SRC;
        script.async = true;
        script.crossOrigin = "anonymous";
        script.referrerPolicy = "no-referrer";

        script.onload = () => {
          // If callback didn't fire, sometimes google object appears anyway
          setTimeout(() => {
            if (this._loaded) return;
            if (window.google?.translate?.TranslateElement) {
              try {
                this._initOnce();
                finish(true);
              } catch (e) {
                finish(false, e);
              }
            }
          }, 400);
        };

        script.onerror = () => finish(false, "script blocked (AdBlock/CSP/network)");

        document.head.appendChild(script);
      }

      // Timeout fallback
      setTimeout(() => {
        if (this._loaded) return;
        const online = typeof navigator !== "undefined" ? navigator.onLine : true;
        finish(false, online ? "timeout (likely blocked by AdBlock/CSP)" : "offline");
      }, this.loadTimeoutMs);
    });

    return this._loadingPromise;
  }

  _initOnce() {
    if (this._inited) return;
    this._inited = true;

    // eslint-disable-next-line no-new
    new window.google.translate.TranslateElement(
      {
        pageLanguage: this.pageLanguage,
        includedLanguages: this.includedLanguages.join(","),
        autoDisplay: false,
      },
      this.containerId
    );
  }

  /* -------------------- language -------------------- */
  _setHtmlLangDir(lang) {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
  }

  _setGoogTransCookie(dst) {
    const v = `/${this.pageLanguage}/${dst}`;
    document.cookie = `googtrans=${v};path=/;SameSite=Lax`;
    document.cookie = `googtrans=${v};path=/;domain=${location.hostname};SameSite=Lax`;
  }

  _dispatchChange(el) {
    el.dispatchEvent(new Event("change", { bubbles: true }));
  }

  getLanguage() {
    return this._current;
  }

  /**
   * Returns { ok: boolean }
   * ok=false when blocked by AdBlock/CSP, but app continues.
   */
  async setLanguage(lang) {
    if (!lang || !this.includedLanguages.includes(lang)) return { ok: false };

    this._current = lang;
    this._save(lang);
    this._setHtmlLangDir(lang);

    const ok = await this.load();
    if (!ok) return { ok: false };

    this._setGoogTransCookie(lang);
    this._cleanupGoogleUiNodes();

    const combo = document.querySelector(".goog-te-combo");
    if (!combo) {
      setTimeout(() => this.setLanguage(lang), 250);
      return { ok: true };
    }

    combo.value = lang;
    this._dispatchChange(combo);

    // Sticky anti-revert
    setTimeout(() => {
      this._cleanupGoogleUiNodes();
      const combo2 = document.querySelector(".goog-te-combo");
      if (!combo2) return;
      if (combo2.value !== this._current) {
        combo2.value = this._current;
        this._setGoogTransCookie(this._current);
        this._dispatchChange(combo2);
      }
    }, 900);

    return { ok: true };
  }

  /* -------------------- SPA re-translate -------------------- */
  _scheduleReapply() {
    clearTimeout(this._timer);
    this._timer = setTimeout(() => {
      if (!this._ignore) this._forceReapply();
    }, this.debounceMs);
  }

  async _forceReapply() {
    const ok = await this.load();
    if (!ok) return;

    const combo = document.querySelector(".goog-te-combo");
    if (!combo) return;

    this._ignore = true;

    combo.value = this._current;
    this._setGoogTransCookie(this._current);
    this._dispatchChange(combo);

    this._cleanupGoogleUiNodes();

    setTimeout(() => {
      this._ignore = false;
    }, 120);
  }

  startObserver() {
    if (this._observer) return;

    this._observer = new MutationObserver((mutations) => {
      if (this._ignore) return;

      let should = false;

      for (const m of mutations) {
        if (m.type !== "childList") continue;
        if (!m.addedNodes || m.addedNodes.length === 0) continue;

        for (const node of m.addedNodes) {
          if (!(node instanceof HTMLElement)) continue;

          // Ignore Google injected frames
          if (node.closest?.(".goog-te-banner-frame, .goog-te-menu-frame")) continue;

          // Ignore areas you mark
          if (node.closest?.("[data-no-translate]")) continue;

          should = true;
          break;
        }
        if (should) break;
      }

      if (should) this._scheduleReapply();
    });

    this._observer.observe(document.body, { childList: true, subtree: true });

    // a couple of cleanups after load (not a permanent interval)
    setTimeout(() => this._cleanupGoogleUiNodes(), 1000);
    setTimeout(() => this._cleanupGoogleUiNodes(), 2500);
  }

  stopObserver() {
    this._observer?.disconnect();
    this._observer = null;
  }
}
